import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  FormErrorMessage,
  Card,
  CardBody,
  Text,
  Badge,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { useEmail, EmailStatusResponse } from '../../hooks/useEmail';

const statusColorMap: Record<string, string> = {
  sent: 'blue',
  delivered: 'green',
  bounced: 'red',
  complained: 'orange',
  pending: 'yellow',
};

export function EmailStatus() {
  const { getEmailStatus, loading, error, clearError } = useEmail();
  const [emailId, setEmailId] = useState('');
  const [status, setStatus] = useState<EmailStatusResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setValidationError(null);
    clearError();

    if (!emailId.trim()) {
      setValidationError('Email ID is required');
      return;
    }

    const result = await getEmailStatus(emailId.trim());

    if (result) {
      setStatus(result);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Box as="form" onSubmit={handleSubmit} mb={6}>
        <VStack spacing={4} align="stretch">
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
              <CloseButton
                position="relative"
                right={-1}
                top={-1}
                onClick={clearError}
              />
            </Alert>
          )}

          <FormControl isRequired isInvalid={!!validationError}>
            <FormLabel>Email ID</FormLabel>
            <HStack>
              <Input
                placeholder="Enter email ID (e.g., ae123...)"
                value={emailId}
                onChange={(e) => setEmailId(e.target.value)}
              />
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText="Checking..."
                px={8}
              >
                Check Status
              </Button>
            </HStack>
            <FormErrorMessage>{validationError}</FormErrorMessage>
          </FormControl>
        </VStack>
      </Box>

      {status && (
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="lg">
                  Email Status
                </Text>
                <Badge
                  colorScheme={statusColorMap[status.status] || 'gray'}
                  fontSize="md"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {status.status.toUpperCase()}
                </Badge>
              </HStack>

              <Divider />

              <Box>
                <Text color="gray.600" fontSize="sm">
                  Email ID
                </Text>
                <Text fontFamily="mono" fontSize="sm">
                  {status.id}
                </Text>
              </Box>

              {status.to && (
                <Box>
                  <Text color="gray.600" fontSize="sm">
                    Recipient
                  </Text>
                  <Text>{status.to}</Text>
                </Box>
              )}

              {status.subject && (
                <Box>
                  <Text color="gray.600" fontSize="sm">
                    Subject
                  </Text>
                  <Text>{status.subject}</Text>
                </Box>
              )}

              <Box>
                <Text color="gray.600" fontSize="sm">
                  Created At
                </Text>
                <Text>{formatDate(status.createdAt)}</Text>
              </Box>

              {status.lastEventAt && (
                <Box>
                  <Text color="gray.600" fontSize="sm">
                    Last Event
                  </Text>
                  <Text>{formatDate(status.lastEventAt)}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}
    </Box>
  );
}
